import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import SpecialOffersContext from '../SpecialOffersContext';
import convertTrimsDataToCardProps from '../utils/convertTrimsDataToCardProps';
import getAllTrims from '../utils/getAllTrims';
import getDefaultSpecialOffersPaymentOptions from '../utils/getDefaultSpecialOffersPaymentOptions';
import getSpecialRateBasedOnCriterion from '../utils/convertTrimsDataToCardProps/getSpecialRateBasedOnCriterion';
import mapOffersToTrims from '../utils/mapOffersToTrims';
import safelyStringifyJSON from '../../../utils/safelyStringifyJSON';
import useFetchAllOffers from './useFetchAllOffers';
import useFetchAllPaymentsWithOffer from './useFetchAllPaymentsWithOffer';
import usePriceFlagsForProvince from '../../../utils/hooks/usePriceFlagsForProvince';
import useSpecialOffersConfiguration from './useSpecialOffersConfiguration';
import { PaymentMethod } from '../constants';
import { filterSpecialPaymentOffers, filterTrimsWithValidSpecialOffers } from '../utils/filterSpecialOffers';
import { selectCanUseLoyalty } from '../../../core/reducers/offerOptions';
import { selectPrimaryCalculatorPaymentOptions } from '../../../core/reducers/financialCalculators';
import { selectTrimsWithPaymentAndOffers, setTrimsWithPaymentAndOffers } from '../../../core/reducers/specialOffers';
import { useAppSelector } from '../../../core/utils';
import { useCreatePaymentRequest } from '../../../core/apiHooks/useFetchPayment';
import { useLazyGetPaymentQuery } from '../../../core/apis/hondaRestApi';
const useFetchTrimOffersAndPayment = (fields, canUseLoyalty) => {
    var _a, _b;
    const dispatch = useDispatch();
    const { catalog: { fields: catalogFields }, } = fields.data.value;
    const models = (_b = (_a = catalogFields.models) === null || _a === void 0 ? void 0 : _a[0].items) !== null && _b !== void 0 ? _b : [];
    const allTrims = models.flatMap(getAllTrims);
    const { data: allOffers, isSuccess: isOfferRequestSuccessful, isFetching: isOfferFetching, isLoading: isOfferLoading, } = useFetchAllOffers(allTrims, canUseLoyalty);
    const isOfferDataLoadedSuccessfully = !isOfferLoading && isOfferRequestSuccessful;
    const trimsWithOffers = useMemo(() => isOfferDataLoadedSuccessfully
        ? mapOffersToTrims(allTrims, filterSpecialPaymentOffers(allOffers, canUseLoyalty))
        : [], [allTrims, allOffers, isOfferDataLoadedSuccessfully, canUseLoyalty]);
    const trimsWithSpecialOffers = filterTrimsWithValidSpecialOffers(trimsWithOffers, canUseLoyalty);
    const { data: _trimsWithPaymentAndOffers, isSuccess: isPaymentRequestSuccessful, isFetching: isPaymentFetching, isLoading: isPaymentLoading, } = useFetchAllPaymentsWithOffer(trimsWithSpecialOffers, isOfferRequestSuccessful && !!trimsWithSpecialOffers.length);
    useEffect(() => {
        if (!_trimsWithPaymentAndOffers.length)
            return;
        dispatch(setTrimsWithPaymentAndOffers(_trimsWithPaymentAndOffers));
    }, [safelyStringifyJSON(_trimsWithPaymentAndOffers), dispatch]);
    const isLoading = isOfferLoading || isOfferFetching || isPaymentFetching || isPaymentLoading;
    const hasErrors = !isLoading && (!isOfferRequestSuccessful || !isPaymentRequestSuccessful);
    return {
        isLoading,
        hasErrors,
    };
};
const useSpecialOffersCards = (fields) => {
    const { modelPrices } = useContext(SpecialOffersContext);
    const { showSellingPrice } = usePriceFlagsForProvince();
    const config = useSpecialOffersConfiguration();
    const canUseLoyalty = useAppSelector(selectCanUseLoyalty);
    const allTrimsWithPaymentAndOffers = useAppSelector(selectTrimsWithPaymentAndOffers);
    const primaryCalculatorPaymentOptions = useAppSelector(selectPrimaryCalculatorPaymentOptions);
    const createPaymentRequest = useCreatePaymentRequest();
    const { isLoading, hasErrors } = useFetchTrimOffersAndPayment(fields, canUseLoyalty);
    const [triggerPaymentFetch] = useLazyGetPaymentQuery();
    const updatePaymentDataForTrim = async (paymentMethod, transmissionKey) => {
        var _a, _b, _c, _d, _e, _f;
        const trim = allTrimsWithPaymentAndOffers.find(trimData => trimData.transmissionKey === transmissionKey);
        if (!trim)
            return;
        const vehicle = {
            modelKey: trim.modelKey,
            modelYear: trim.modelYear,
            trimKey: trim.trimKey,
            transmissionKey,
            exteriorColorKey: trim.exteriorColorKey,
            interiorColorKey: trim.interiorColorKey,
            protections: [],
            protectionAddOns: [],
        };
        const { defaultFrequency: defaultPaymentFrequency, defaultTerm, defaultDownPayment, defaultIncentiveOffers, loyaltyOfferKey, } = getDefaultSpecialOffersPaymentOptions(trim, config, paymentMethod);
        const specialRateOffers = getSpecialRateBasedOnCriterion(trim.offers.specialRateOffers, (_c = (_b = (_a = trim.defaultTransmission) === null || _a === void 0 ? void 0 : _a.specialRateCriterion) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '');
        const termAndRate = (paymentMethod !== PaymentMethod.cash && ((_d = specialRateOffers[paymentMethod]) === null || _d === void 0 ? void 0 : _d.term)) || defaultTerm;
        const paymentFrequency = defaultPaymentFrequency;
        const incentiveOfferIds = (_f = (_e = trim.offers.incentiveOffers[paymentMethod]) === null || _e === void 0 ? void 0 : _e.map(offer => offer.id)) !== null && _f !== void 0 ? _f : defaultIncentiveOffers;
        const { url, payload, languageCode } = createPaymentRequest({
            vehicle,
            paymentOptions: {
                ...primaryCalculatorPaymentOptions,
                termAndRate,
                incentiveOfferIds,
                loyaltyOfferKey: (canUseLoyalty && loyaltyOfferKey) || '',
                downPaymentAmount: defaultDownPayment,
                paymentFrequency,
                paymentMethod,
            },
        });
        triggerPaymentFetch({ url, body: payload, languageCode });
    };
    const { ctaLink1, ctaLink2, ctaType1, ctaType2 } = fields.data.value;
    const specialOffersCards = convertTrimsDataToCardProps({
        trimsWithPaymentAndOffers: allTrimsWithPaymentAndOffers.map(trim => ({
            ...trim,
            pricing: modelPrices[trim.transmissionId],
        })),
        primaryCta: { ...ctaLink1.item.value, styling: ctaType1.value },
        secondaryCta: { ...ctaLink2.item.value, styling: ctaType2.value },
        hasLoyaltyOffer: canUseLoyalty,
        showSellingPrice,
        config,
        onPaymentMethodChange: updatePaymentDataForTrim,
    });
    const isEmpty = !isLoading && !specialOffersCards.length;
    return {
        specialOffersCards,
        isLoading,
        hasErrors,
        isEmpty,
    };
};
export default useSpecialOffersCards;
